import { useTranslation } from "react-i18next"
import { Stack, Typography } from "@mui/material"

import type { Powerline } from "types/powerlines.types"
import type { Span } from "types/spans.types"

import useRoutes from "helpers/hooks/useRoutes"
import CustomLink from "widgets/common/CustomLink"
import DetailsGroupsCard from "widgets/common/DetailsGroupsCard"
import SpanTowerLink from "widgets/spans/SpanTowerLink"
import { getEntityHref } from "helpers/utils/entities"
import { getSpanDetailsGroups } from "helpers/utils/spans"
import { nameOrUnnamed } from "helpers/utils/translations"

interface SpanDetailsProps {
  span: Span
  powerline?: Powerline
}

const SpanDetails = ({ span, powerline }: SpanDetailsProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"
  const { baseOrgURL } = useRoutes()

  // The line is always there to link to, its name only once it has loaded.
  const powerlineLink = (
    <CustomLink
      href={getEntityHref("powerline", span.powerline_id, baseOrgURL)}
      underlineHover
    >
      {powerline ? nameOrUnnamed(powerline.name, t) : span.powerline_id}
    </CustomLink>
  )

  const towers =
    span.tower_ids.length > 0 ? (
      <Stack flexDirection="row" flexWrap="wrap" columnGap={0.5}>
        {span.tower_ids.map((towerId, index) => (
          <Typography key={towerId}>
            <SpanTowerLink towerId={towerId} />
            {index < span.tower_ids.length - 1 && ","}
          </Typography>
        ))}
      </Stack>
    ) : undefined

  return (
    <DetailsGroupsCard
      titleKey="spans.SPAN_DETAILS"
      groups={getSpanDetailsGroups(span, locale, t, { powerlineLink, towers })}
    />
  )
}

export default SpanDetails
